/**
 * TanStack Router setup (code-based route tree).
 *
 * This replaces the old dual routing system (Bun's server-side FileSystemRouter
 * plus the hand-maintained client registry in `config/routes.tsx`). The app is a
 * client-rendered SPA: the server serves a single HTML shell for every app route
 * and this router decides which page to render. Navigating between pages swaps
 * only the <Outlet/> content instead of triggering a full-page reload, so the
 * provider tree (realtime connection, data caches, etc.) stays mounted.
 *
 * Search params are intentionally handled with plain URLSearchParams semantics so
 * the router stays compatible with the app's existing `useQueryParams` store,
 * which reads/writes `location.search` directly. The default TanStack search
 * (de)serializer JSON-encodes values (wrapping strings in quotes); overriding it
 * here keeps URLs like `?office=a,b` flat and unquoted.
 */
import {
  createRootRoute,
  createRoute,
  createRouter,
} from "@tanstack/react-router";
import { App } from "./App";
import { notifyQueryParamChange } from "./hooks/useQueryParams";
import Projects from "./pages/index";
import Makers from "./pages/makers";
import ActionItems from "./pages/action-items";
import AuditLog from "./pages/audit-log";
import Settings from "./pages/settings";

/**
 * Loose, pass-through search validation. The app owns its own query-param
 * conventions (comma-joined lists, page-scoped tab names, etc.) via
 * `useQueryParams`, so the router treats every param as an optional string
 * rather than imposing a schema. This also makes `<Link search={...}>` accept
 * arbitrary string params without per-route typing.
 */
function validateSearch(
  search: Record<string, unknown>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(search)) {
    if (value != null) out[key] = String(value);
  }
  return out;
}

const rootRoute = createRootRoute({
  component: App,
  validateSearch,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: Projects,
});

const makersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/makers",
  component: Makers,
});

const actionItemsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/action-items",
  component: ActionItems,
});

const auditLogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/audit-log",
  component: AuditLog,
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  component: Settings,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  makersRoute,
  actionItemsRoute,
  auditLogRoute,
  settingsRoute,
]);

export const router = createRouter({
  routeTree,
  // Flat, URLSearchParams-compatible search handling (see file header).
  parseSearch: (searchStr) =>
    Object.fromEntries(new URLSearchParams(searchStr)),
  stringifySearch: (search) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(search)) {
      if (value != null && value !== "") params.set(key, String(value));
    }
    const serialized = params.toString();
    return serialized ? `?${serialized}` : "";
  },
});

// Bridge router navigations into the `useQueryParams` store. A `<Link>` that
// only changes search params on the current path updates history via pushState
// (no `popstate` event), so query-param readers wouldn't otherwise refresh.
router.subscribe("onResolved", () => notifyQueryParamChange());

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
